import React, { useRef, useLayoutEffect } from 'react';
import { BattleUnitState, TurnOrderEntry, HotbarSkill, StatusEffect } from '../types/battle';
import { getHealthColor, rotateTurnOrder } from '../utils/battleUtils';

interface BattleTurnOrderPanelProps {
  turnOrder: TurnOrderEntry[];
  units: BattleUnitState[];
  currentTurnUnit: TurnOrderEntry | null;
  attackMode: boolean;
  skillMode: boolean;
  validTargets: BattleUnitState[];
  skillTargets: { x: number; y: number }[];
  selectedUnit: BattleUnitState | null;
  selectedSkill: HotbarSkill | null;
  battleStatus: string;
  turnTimeRemaining: number;
  showTimer?: boolean;
  onAttackUnit: (selectedUnitId: number, targetUnitId: number) => void;
  onExecuteSkill: (x: number, y: number) => void;
  onSelectUnit: (unit: BattleUnitState) => void;
  // Enemy threat-range overlay (GH-69): tapping an enemy's portrait outside of
  // attack/skill mode toggles a danger-zone highlight for just that unit.
  threatOverlayUnitId?: number | null;
  onToggleThreatUnit?: (unitId: number) => void;
}

function getStatusEffectIcon(effectType: string): string {
  switch (effectType) {
    case 'Poison': return '☠️';
    case 'Burn': return '🔥';
    case 'Regeneration': return '💚';
    case 'DamageReduction': return '🛡️';
    case 'Stun': return '💫';
    case 'Root': return '🌿';
    case 'DamageBuff': return '⚔️';
    case 'Invisibility': return '👻';
    case 'StatBuff': return '✨';
    default: return '✨';
  }
}

const BattleTurnOrderPanel: React.FC<BattleTurnOrderPanelProps> = ({
  turnOrder,
  units,
  currentTurnUnit,
  attackMode,
  skillMode,
  validTargets,
  skillTargets,
  selectedUnit,
  selectedSkill,
  battleStatus,
  turnTimeRemaining,
  showTimer = true,
  onAttackUnit,
  onExecuteSkill,
  onSelectUnit,
  threatOverlayUnitId,
  onToggleThreatUnit,
}) => {
  // Rotating queue (GH-89): the acting unit renders leftmost; finished units wrap to
  // the back. FLIP-animate reorders so the finished card visibly slides to the far
  // right instead of teleporting. Cards are keyed by unitId, so React reuses the DOM
  // nodes across reorders - that's what makes measuring prev/next offsets meaningful.
  const rotatedOrder = rotateTurnOrder(turnOrder ?? [], currentTurnUnit?.unitId);
  const listRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const prevLeftsRef = useRef<Map<number, number>>(new Map());
  const orderSignature = rotatedOrder.map(u => u.unitId).join(',');

  useLayoutEffect(() => {
    const prevLefts = prevLeftsRef.current;
    const nextLefts = new Map<number, number>();
    cardRefs.current.forEach((el, unitId) => nextLefts.set(unitId, el.offsetLeft));

    cardRefs.current.forEach((el, unitId) => {
      const prev = prevLefts.get(unitId);
      const next = nextLefts.get(unitId);
      if (prev === undefined || next === undefined || prev === next) return;
      // FLIP: jump the card back to where it was, then let it transition to its new
      // slot. Double-rAF guarantees the browser paints the inverted position before
      // the transition starts (same restart trick as useBattleEffects' screen shake).
      el.style.transition = 'none';
      el.style.transform = `translateX(${prev - next}px)`;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.transition = 'transform 0.35s ease';
          el.style.transform = '';
        });
      });
    });

    prevLeftsRef.current = nextLefts;
    // Keep the current (leftmost) card in view on the scrollable strip.
    // (scrollTo is missing in jsdom, hence the feature check.)
    const list = listRef.current;
    if (list) {
      if (typeof list.scrollTo === 'function') list.scrollTo({ left: 0, behavior: 'smooth' });
      else list.scrollLeft = 0;
    }
  }, [orderSignature]);

  return (
    <div className="turn-order-section">
      <h3 className="turn-order-label">Turn Order</h3>
      <div className="turn-list-horizontal" ref={listRef}>
        {rotatedOrder.map((turnUnit: TurnOrderEntry) => {
          const isCurrent = currentTurnUnit && turnUnit.unitId === currentTurnUnit.unitId;
          const fullUnit = units?.find((u) => u.id === turnUnit.unitId);
          const canToggleThreat = !turnUnit.isPlayerUnit && !attackMode && !skillMode && !!onToggleThreatUnit;
          const isClickable = turnUnit.isPlayerUnit || (!turnUnit.isPlayerUnit && (attackMode || skillMode)) || canToggleThreat;
          const isThreatSelected = threatOverlayUnitId === turnUnit.unitId;

          return (
            <div
              key={turnUnit.unitId}
              ref={(el) => {
                if (el) cardRefs.current.set(turnUnit.unitId, el);
                else cardRefs.current.delete(turnUnit.unitId);
              }}
              className={`turn-item-horizontal ${isCurrent ? 'current-turn' : ''} ${turnUnit.isPlayerUnit ? 'player-unit' : 'enemy-unit'} ${isClickable ? 'clickable' : ''} ${isThreatSelected ? 'threat-selected' : ''}`}
              onClick={() => {
                if (attackMode || skillMode) {
                  if (!turnUnit.isPlayerUnit && attackMode && selectedUnit && fullUnit) {
                    if (validTargets.some(target => target.id === fullUnit.id)) {
                      onAttackUnit(selectedUnit.id, fullUnit.id);
                    }
                    return;
                  }
                  if (skillMode && selectedSkill && selectedUnit && fullUnit) {
                    if (skillTargets.some(target => target.x === fullUnit.position.x && target.y === fullUnit.position.y)) {
                      onExecuteSkill(fullUnit.position.x, fullUnit.position.y);
                    }
                    return;
                  }
                }
                if (turnUnit.isPlayerUnit && fullUnit) {
                  onSelectUnit(fullUnit);
                  return;
                }
                if (canToggleThreat) {
                  onToggleThreatUnit!(turnUnit.unitId);
                }
              }}
              style={{ cursor: isClickable ? 'pointer' : 'default' }}
              title={
                attackMode && !turnUnit.isPlayerUnit
                  ? 'Click to attack'
                  : skillMode && !turnUnit.isPlayerUnit
                  ? 'Click to cast skill'
                  : skillMode && turnUnit.isPlayerUnit && fullUnit && skillTargets.some(t => t.x === fullUnit.position.x && t.y === fullUnit.position.y)
                  ? 'Click to cast skill'
                  : turnUnit.isPlayerUnit
                  ? 'Click to view action bar'
                  : canToggleThreat
                  ? (isThreatSelected ? 'Click to hide threat range' : 'Click to show this enemy\'s threat range')
                  : ''
              }
            >
              <div className="turn-unit-header">
                <span className="turn-name-horizontal">{turnUnit.name}</span>
                <span className="turn-initiative-horizontal">{turnUnit.initiative}</span>
              </div>

              {fullUnit && (
                <div className="turn-unit-stats">
                  <div className="stat-bar hp-bar">
                    <div className="stat-label">HP</div>
                    <div className="stat-bar-container">
                      <div className="stat-bar-bg">
                        <div
                          className="stat-bar-fill hp-fill"
                          style={{
                            width: `${Math.max(0, (fullUnit.stats.currentHP / fullUnit.stats.maxHP) * 100)}%`,
                            backgroundColor: getHealthColor(fullUnit.stats.currentHP / fullUnit.stats.maxHP)
                          }}
                        />
                      </div>
                      <span className="stat-text">{fullUnit.stats.currentHP}/{fullUnit.stats.maxHP}</span>
                    </div>
                  </div>

                  {fullUnit.statusEffects && fullUnit.statusEffects.length > 0 && (
                    <div className="turn-status-effects">
                      {fullUnit.statusEffects.map((effect: StatusEffect, idx: number) => (
                        <span key={idx} className="status-effect-icon" title={`${effect.effectType} (${effect.turnsRemaining} turns)`}>
                          {getStatusEffectIcon(effect.effectType)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {showTimer && battleStatus === 'InProgress' && currentTurnUnit && (
        <div className={`turn-timer ${turnTimeRemaining <= 10 ? 'timer-warning' : ''}`}>
          <span className="timer-icon">⏱️</span>
          <span className="timer-text">{turnTimeRemaining}s</span>
        </div>
      )}
    </div>
  );
};

export default BattleTurnOrderPanel;
